import React, { useCallback, useEffect, useState } from "react";
import {
    ArchiveConstants,
    DEFAULT_PAGE_SIZE,
    MyFilesConstants,
} from "src/Core/Common/Constants";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "src/store";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { Col, FormControl, Row } from "react-bootstrap";
import { DownloadIcon, RefreshIcon, SearchIcon } from "src/assets/images/svg/IconCollection";
import ArchiveFileTable from "./ArchiveFileTable";
import { IMyFilesFileModel } from "src/Core/ViewModels/File/MyFilesFileModel";
import { fetchFolderFileViewData, requestDownloadNow } from "src/actionCreators/folderActionCreators";
import { debounce } from "throttle-debounce";
import { getDownloadPayload, getMailboxUserEmailId, getTotalFileSize } from "src/helper/HelperFunctions";
import { downloadUploadedFile } from "src/actionCreators/documentRequestActionCreators";
import { getFolderTypeForArchiveFolder, handleThreadsDownloadSingleAttachment } from "src/helper/MessageHelperThreads";
import { MessageSource, MyFilesFileType } from "src/Core/Common/Enums";
import { getYearFromDate } from "src/helper/DateHelperFunctions";
import { CustomModal } from "../Common/Modal/CustomModal";
import { fetchCPAPortalUrl } from "src/actionCreators/commonActionCreators";

const initialPagination = {
    pageSize: DEFAULT_PAGE_SIZE,
    currentPage: 1,
    totalRecords: 0,
};

interface IArchiveFileProps {
    folderId: number;
    folder?: IFolderModel;
}


const ArchiveFiles: React.FC<IArchiveFileProps> = props => {
    const [searchTextBox, setSearchTextBox] = useState<string>("");
    const [searchText, setSearchText] = useState<string>("");
    const { mailboxUserData } = useSelector((state: ApplicationState) => state.commonState);
    const dispatch = useDispatch();
    const defaultFileViewSort = {
        dataField: "fileName",
        order: "asc",
    };
    const [fileViewSort, setFileViewSort] = useState(defaultFileViewSort);
    const [selectedFiles, setSelectedFiles] = useState<IMyFilesFileModel[]>([]);
    const [currentFilesPageNumber, setCurrentFilesPageNumber] = useState<number>(initialPagination.currentPage);
    const [reload, setReload] = useState(false);
    const isFileDownloading = useSelector(
        (appState: ApplicationState) => appState.folderState.fileViewData.isDownloading
    );
    const isDownloadIconDisabled = !selectedFiles.length || isFileDownloading;
    const [showDownloadSizeExceededModal, setShowDownloadSizeExceededModal] = useState(false);
    const cpaPortalUrl = useSelector((appState: ApplicationState) => appState.commonState.cpaPortalUrl);

    /** Reset Fileview Search */
    const handleResetSearchAndSort = () => {
        setSearchTextBox("");
        setSearchTextBox("");
        setFileViewSort(defaultFileViewSort);
    };

    const signalRState = useSelector((appState: ApplicationState) => appState.signalRState);

    useEffect(() => {
        if (searchTextBox === "") {
            setSelectedFiles([]);
        }
    }, [searchTextBox]);

    useEffect(() => {
        dispatch(fetchCPAPortalUrl());
    }, []);

    useEffect(() => {
        dispatch(fetchFolderFileViewData(
            props.folderId,
            currentFilesPageNumber,
            DEFAULT_PAGE_SIZE,
            getMailboxUserEmailId(),
            searchTextBox,
            fileViewSort.dataField,
            fileViewSort.order
        ));
    }, [mailboxUserData, currentFilesPageNumber, fileViewSort.order, searchText]);

    useEffect(() => {
        setSearchTextBox("");
        setFileViewSort(defaultFileViewSort);
        setCurrentFilesPageNumber(initialPagination.currentPage);
        dispatch(
            fetchFolderFileViewData(
                props.folderId,
                currentFilesPageNumber,
                DEFAULT_PAGE_SIZE,
                "",
                "",
                fileViewSort.dataField,
                fileViewSort.order
            )
        );

    }, [reload, signalRState.archiveFolderReload]);

    const getData = (value: string) => {
        //setSearchtext to re-render components
        setSearchText(value);
        setCurrentFilesPageNumber(initialPagination.currentPage);
    };


    const debounceFn = useCallback(debounce(1000, getData), []);

    const onFilter: React.ChangeEventHandler<HTMLInputElement> = e => {
        const value = (e.target as HTMLInputElement).value.toLocaleLowerCase();
        //Set only search box
        setSearchTextBox(value);
        if (value == "") {
            setSelectedFiles([]);
        }
        debounceFn(value);
    };

    const handleOnPageChange = (pageNo: number, pageSize: number = DEFAULT_PAGE_SIZE) => {
        setCurrentFilesPageNumber(pageNo);
    };

    const handleDRDownloadSingleAttachment = (fileGuid: string, sentDate: Date, fileName: string) => {
        dispatch(
            downloadUploadedFile(
                fileName,
                fileGuid,
                getYearFromDate(sentDate),
                ResourceIdLocators.DRL.DRLBtnDownloadFile,
                downloadSingleAttachmentCallback
            )
        );
    };

    const handleMessageDownloadSingleAttachment = (
        fileId: number,
        physicalPath: string,
        messageId: number,
        fileName: string,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        folderId?: number
    ) => {
        const updatedFolderType = getFolderTypeForArchiveFolder(folderId);
        handleThreadsDownloadSingleAttachment(
            dispatch,
            fileId,
            physicalPath,
            messageId,
            fileName,
            senderEmail,
            receivedMessageGuid,
            messageSource,
            updatedFolderType,
            downloadSingleAttachmentCallback
        );
    };

    const downloadSingleAttachmentCallback = (result: any) => {
        if (result) {
            //reload UI after download
            dispatch(fetchFolderFileViewData(
                props.folderId,
                currentFilesPageNumber,
                DEFAULT_PAGE_SIZE,
                getMailboxUserEmailId(),
                searchTextBox,
                fileViewSort.dataField,
                fileViewSort.order
            ));
            window.open(result, "_blank");
        }
    };


    const downloadCallback = () => {
        dispatch(fetchFolderFileViewData(props.folderId, initialPagination.currentPage, initialPagination.pageSize, "", ""));
        setSelectedFiles([]);
        handleResetSearchAndSort();
    };

    const handleDownloadButtonClick: any = () => {
        if (selectedFiles.length == 1) {
            const file = selectedFiles[0];
            if (file.fileType === MyFilesFileType.DocumentRequest) {
                handleDRDownloadSingleAttachment(file.fileGuid, file.sentOrReceivedDate, file.fileName);
            } else {
                handleMessageDownloadSingleAttachment(
                    file.fileId,
                    file.fileGuid,
                    file.referenceId,
                    file.fileName,
                    file.senderEmail,
                    file.referenceGuid,
                    file.messageSource,
                    file.fileType
                );
            }
        } else if (getTotalFileSize(selectedFiles) > MyFilesConstants.MAX_FILE_SIZE) {
            setShowDownloadSizeExceededModal(true);
        } else {
            const downloadPayload = getDownloadPayload(selectedFiles);
            dispatch(requestDownloadNow(downloadPayload, downloadCallback, ResourceIdLocators.MyFiles.MyFilesBtnDownloadFiles));
        }
    };

    return (
        <div id="archive-files" className="flex-row search-box-no-border-radius">
            <div className="message-list-wrapper">
                <Row className="search-header-wrapper">
                    <Col className="search-input-wrapper-col col-8">
                        <div className="search-input-wrapper">
                            <FormControl
                                placeholder={ArchiveConstants.ShareFolder.PLACEHOLDER_SEARCH_TEXT}
                                value={searchTextBox}
                                onChange={onFilter}
                                className="search-input search-textbox"
                            />
                            <div className="search-icon">
                                <SearchIcon />
                            </div>
                        </div>
                    </Col>
                    <Col className="refresh-button-container col-2">
                        <div
                            className={`refresh-button-wrapper ${isDownloadIconDisabled ? "disabled" : ""}`}
                            data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnDownloadFiles}
                            onClick={(event) => {
                                !isDownloadIconDisabled && handleDownloadButtonClick(event);
                            }}

                        >
                            <DownloadIcon disabled={isDownloadIconDisabled} />
                        </div>
                    </Col>
                    <Col className="refresh-button-container col-2">
                        <div
                            className="refresh-button-wrapper"
                            title={ArchiveConstants.REFRESH_TITLE}
                            onClick={() => {
                                setReload(!reload);
                            }}
                        >
                            <RefreshIcon />
                        </div>
                    </Col>
                </Row>
                <ArchiveFileTable
                    currentFilesPageNumber={currentFilesPageNumber}
                    setCurrentPageNumber={setCurrentFilesPageNumber}
                    currentFolder={props.folder}
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    sortBy={fileViewSort}
                    setSortBy={setFileViewSort}
                    onMessageDownloadSingleAttachment={handleMessageDownloadSingleAttachment}
                    onDRDownloadSingleAttachment={handleDRDownloadSingleAttachment}
                />
                <CustomModal title={MyFilesConstants.MAX_FILE_SIZE_EXCEEDED_MODAL_TITLE}
                    show={showDownloadSizeExceededModal}
                    onHide={() => setShowDownloadSizeExceededModal(false)}
                    onSubmit={() => {
                        setShowDownloadSizeExceededModal(false);
                        setSelectedFiles([]);
                        handleResetSearchAndSort();
                        window.open(cpaPortalUrl, "_blank");
                    }
                    }
                    confirmButtonName="Login Exchange">
                    <p>
                        {MyFilesConstants.MAX_FILE_SIZE_EXCEEDED_MODAL_MESSAGE}
                    </p>
                </CustomModal>
            </div>
        </div>
    );
};

export default ArchiveFiles;
